import { IMediaRecorder, MediaRecorder } from 'extendable-media-recorder';
import { useEffect, useRef } from 'react';

export default function useVoiceMemoRecorder(options: {
	stream?: MediaStream;
	sampleRate: number;
	onSample: (amplitude: number) => void;
	onAmplitude: (amplitude: number) => void;
	onFinish: (blob: Blob) => void;
}) {
	const ref = useRef({} as { recorder: IMediaRecorder }).current;
	const blobs = useRef<Blob[]>([]).current;

	const onAmplitude = useRef(options.onAmplitude);
	onAmplitude.current = options.onAmplitude;

	const onSample = useRef(options.onSample);
	onSample.current = options.onSample;

	const onFinish = useRef(options.onFinish);
	onFinish.current = options.onFinish;

	useEffect(() => {
		const context = new AudioContext();

		ref.recorder = new MediaRecorder(options.stream!, { mimeType: 'audio/wav' });

		const analyser = context.createAnalyser();
		const source = context.createMediaStreamSource(options.stream!);
		source.connect(analyser);

		const data = new Float32Array(analyser.fftSize);

		const avgs: number[] = [];

		const interval = setInterval(() => {
			analyser.getFloatTimeDomainData(data);
			const amplitude = Math.max(...data.map(Math.abs));
			avgs.push(amplitude);
			onAmplitude.current(amplitude);
		}, 100);

		ref.recorder.ondataavailable = async (event) => {
			blobs.push(event.data);

			analyser.getFloatTimeDomainData(data);
			const sample = avgs.splice(0, avgs.length);
			onSample.current(Math.max(...sample));
		};
		ref.recorder.onstop = () => {
			onFinish.current(new Blob(blobs, { type: 'audio/wav' }));
		};
		ref.recorder.start(1000 / options.sampleRate);

		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!options.stream]);

	return {
		stop: () => {
			ref.recorder.stop();
			options.stream?.getTracks().forEach((track) => track.stop());
		},
	};
}
